import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarCheck, Gift, Users, ArrowUpRight } from "lucide-react";
import { api, errText } from "../../lib/admin";
import { Loading } from "./ui";
import { RemindersPanel } from "./RemindersPanel";
import { ReviewsPanel } from "./ReviewsPanel";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (k) => { const [y, m] = k.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" }); };
const shift = (k, n) => { const [y, m] = k.split("-").map(Number); return monthKey(new Date(y, m - 1 + n, 1)); };
const eur = (n) => n.toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const Stat = ({ icon: Icon, label, value, sub, to, testId }) => (
  <Link to={to} data-testid={testId} className="group bg-white border border-[#e7dfd3] rounded-2xl p-6 flex flex-col justify-between min-h-[190px] transition-colors duration-200 hover:border-[#8A6A3A]">
    <div className="flex items-center justify-between text-[#8A6A3A]">
      <span className="flex items-center gap-2 text-[0.7rem] tracking-[0.18em] uppercase"><Icon className="w-4 h-4" /> {label}</span>
      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
    </div>
    <div>
      <p className="font-serif-d font-light text-6xl text-[#14110F] leading-none" data-testid={`${testId}-value`}>{value}</p>
      <p className="mt-3 text-sm text-[#4f4841]">{sub}</p>
    </div>
  </Link>
);

export const SummaryAdmin = () => {
  const [month, setMonth] = useState(monthKey(new Date()));
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    api.get("/admin/summary", { params: { month } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) { toast.error(errText(e)); setData(false); } });
    return () => { alive = false; };
  }, [month]);
  const isCurrent = month === monthKey(new Date());
  const a = data?.appointments ?? {};
  const g = data?.gift_cards ?? {};
  const r = data?.referrals ?? {};
  const top = Array.isArray(data?.top_services) ? data.top_services : [];
  const max = top[0]?.[1] || 1;
  return (
    <div className="space-y-8" data-testid="admin-summary">
      <RemindersPanel />
      <ReviewsPanel />
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setMonth(shift(month, -1))} aria-label="Mese precedente" className="p-2 rounded-full border border-[#ddd4c7] hover:border-[#8A6A3A]" data-testid="summary-prev-month"><ChevronLeft className="w-4 h-4" /></button>
        <p className="font-serif-d text-2xl sm:text-3xl font-light capitalize min-w-[10ch] text-center" data-testid="summary-month">{monthLabel(month)}</p>
        <button type="button" onClick={() => setMonth(shift(month, 1))} disabled={isCurrent} aria-label="Mese successivo" className="p-2 rounded-full border border-[#ddd4c7] hover:border-[#8A6A3A] disabled:opacity-30" data-testid="summary-next-month"><ChevronRight className="w-4 h-4" /></button>
      </div>
      {data === null ? <Loading /> : data === false ? null : (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <Stat icon={CalendarCheck} label="Prenotazioni" value={a.total ?? 0} to="/admin/prenotazioni" testId="summary-appointments"
              sub={`${a.confirmed ?? 0} confermate · ${a.pending ?? 0} da confermare · ${a.cancelled ?? 0} annullate`} />
            <Stat icon={Gift} label="Buoni regalo" value={g.count ?? 0} to="/admin/buoni" testId="summary-gifts"
              sub={`${eur(g.revenue ?? 0)} incassati · ${g.redeemed ?? 0} usati nel mese`} />
            <Stat icon={Users} label="Amiche portate" value={r.friends ?? 0} to="/admin/amiche" testId="summary-referrals"
              sub={`${r.new_members ?? 0} nuove iscritte a "Porta un'amica"`} />
          </div>
          <div className="bg-white border border-[#e7dfd3] rounded-2xl p-6" data-testid="summary-top-services">
            <p className="text-[0.7rem] tracking-[0.18em] uppercase text-[#8A6A3A]">Trattamenti più richiesti</p>
            {top.length ? (
              <ul className="mt-5 space-y-3">
                {top.map(([name, n]) => (
                  <li key={name} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[14rem_minmax(0,1fr)_2rem] items-center gap-x-4 gap-y-1">
                    <span className="text-[#14110F] truncate">{name}</span>
                    <span className="sm:order-last text-right text-sm text-[#4f4841]">{n}</span>
                    <span className="col-span-2 sm:col-span-1 h-2 rounded-full bg-[#efe9df] overflow-hidden"><span className="block h-full bg-[#C9A66B] rounded-full" style={{ width: `${(n / max) * 100}%` }} /></span>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-4 text-[#6b625a]">Nessuna prenotazione in questo mese.</p>}
          </div>
        </>
      )}
    </div>
  );
};
